/****************************************************************************************
 *  ROTATION MANAGER  —  Finite-state, stall-aware, order-checking                       *
 *                                                                                      *
 *  Valid sequence:                                                                      *
 *      Front  →  Rotated  →  Back  →  Rotated  →  Front (done)                         *
 *                                                                                      *
 *  Any deviation (e.g. Front→Back, Rotated→Front, Back→Front, reverse spin, etc.)      *
 *  triggers an error *immediately*.                                                     *
 ****************************************************************************************/

import { KeyPoint } from "@/types/interfaces";
import { RotationState } from "@/types/enums";
import { ScanRotationError } from "@/atoms/scanning";
import {
  PoseDetectionManager,
  RotationPoseEvent,
} from "./detection/PoseDetectionManager";
import {
  getLastMotionViolation,
  clearLastMotionViolation,
} from "@/hooks/useDeviceMotion";
import { logInfo } from "@/Utils/logger";

/* ----------------------------------- CONSTANTS ----------------------------------- */

const MAX_TICKS = 44; // 1 … 44   (≈ 11 s total)
const MIN_ROTATION_FRAMES = 18; // ≈ 4.5 s
const UNKNOWN_TICKS_LIMIT = 4; // consecutive Unknown ⇒ error

const FORWARD_STATE_THRESHOLD = -0.85;
const BACK_STATE_THRESHOLD = 0.85;
const MAX_JOINT_DIST_THRESHOLD = 1.3;
const MIN_JOINT_DIST_THRESHOLD = -1.1;
const MID_ROTATION_THRESHOLD = 0.25;
const FLIP_CORRECTION_THRESHOLD = 0.5; // only correct front-back flips

const LEFT_PARTS = ["left_shoulder", "left_hip", "left_elbow"] as const;
const RIGHT_PARTS = ["right_shoulder", "right_hip", "right_elbow"] as const;

const MAX_WRONG_STATES = 2;

/* ---------------------------------- EVENT TYPES ---------------------------------- */

export type RotationEventData = RotationPoseEvent & {
  state: RotationState;
  gravityString: string;
  keypoints: KeyPoint[];
  lastNorm: number;
};

export type RotationEventMap = {
  rotationError: ScanRotationError | null;
  rotationProgress: RotationEventData;
  rotationSuccess: RotationEventData;
  rotationCompleted: void;
  rotationReset: void;
};

export const RotationEvents = {
  ERROR: "rotationError",
  PROGRESS: "rotationProgress",
  SUCCESS: "rotationSuccess",
  COMPLETED: "rotationCompleted",
  RESET: "rotationReset",
} as const;

/* ----------------------------- HELPER ENUM (FSM) ----------------------------- */

const enum Phase {
  StartFront, // expecting Front
  Side1, // expecting first Rotated
  Back, // expecting Back
  Side2, // expecting second Rotated
  EndFront, // expecting final Front
  Done, // success emitted
}

/* ---------------------------- SINGLETON CONSTRUCTION --------------------------- */

const GLOBAL_KEY = "__ttf-rotationManagerInstance" as const;
declare global {
  interface Window {
    [GLOBAL_KEY]: RotationManager;
  }
}

/* ================================== CLASS ====================================== */

export class RotationManager {
  /* ---------- singleton plumbing ---------- */
  static getInstance() {
    if (!window[GLOBAL_KEY]) window[GLOBAL_KEY] = new RotationManager();
    return window[GLOBAL_KEY];
  }
  private constructor() {
    this.poseDetectionMgr.on("rotationPose", (e) => this.processPose(e));
    // The spin's frames stopped changing: cancel it with an error that says
    // so, instead of judging a still picture (videoFreeze.ts). A freeze before
    // the spin is `useDetectionEffects`'s: the error screen does not listen to
    // this manager then, and before a page load's first spin it may not exist.
    this.poseDetectionMgr.on("cameraFrozen", (e) => {
      if (e.phase === "rotation") this.pushError("camera-frozen", "Camera stopped");
    });
  }

  /* ------------------- STATE ------------------ */

  private listeners = new Map<keyof RotationEventMap, Set<(p: any) => void>>();

  private phase: Phase = Phase.StartFront;
  private sameStateTicks = 0;
  private unknownTicks = 0;
  private lastState: RotationState = RotationState.Unknown;
  private startTick = -1;
  private endTick = -1;
  private wrongStates = 0;

  /* distance bookkeeping (still needed for classification) */
  private maxNorm = Number.NEGATIVE_INFINITY;
  private minNorm = Number.POSITIVE_INFINITY;
  private lastNorm = -1;
  private jointsInit: Record<string, number> = {};

  /* misc */
  private gravityString = "";
  private latestPoseEvt: RotationPoseEvent | null = null;
  private internalError: ScanRotationError | null = null;
  private stationary = true;

  private poseDetectionMgr = PoseDetectionManager.getInstance();

  /* -------------------- Event API -------------------- */
  on<K extends keyof RotationEventMap>(
    e: K,
    fn: (p: RotationEventMap[K]) => void,
  ) {
    if (!this.listeners.has(e)) this.listeners.set(e, new Set());
    this.listeners.get(e)?.add(fn as any);
    return () => this.listeners.get(e)?.delete(fn as any);
  }
  private emit<K extends keyof RotationEventMap>(e: K, p: RotationEventMap[K]) {
    this.listeners.get(e)?.forEach((cb) => cb(p as any));
  }

  /* -------------------- Public setters ------------------- */
  setGravityString(s: string) {
    this.gravityString = s;
  }
  setStationary(b: boolean) {
    this.stationary = b;
  }

  /* ------------------------- MAIN LOOP ------------------------ */
  private processPose(evt: RotationPoseEvent) {
    // A reply from a spin that already ended — failed, or abandoned. Its frame
    // must not reach ScanUploader: the next spin's upload has the same slots.
    // `internalError` below cannot catch it, because `pushError` clears it in
    // `reset()` on the very next line.
    if (evt.run !== this.poseDetectionMgr.getRotationRun()) return;
    const { ticksCount, poses } = evt;
    this.latestPoseEvt = evt;
    if (!this.stationary) {
      this.pushError("device-moved", "Device moved");
      return;
    }
    if (ticksCount > MAX_TICKS) {
      this.emit(RotationEvents.COMPLETED, undefined);
      return;
    }
    if (this.internalError) return; // skip frames after error
    const keypoints = this.toKeyPoints(poses[0]);
    const state = this.classifyState(keypoints);
    this.emit(RotationEvents.PROGRESS, {
      ...evt,
      state,
      gravityString: this.gravityString,
      keypoints,
      lastNorm: this.lastNorm,
    });
    this.updateStallCounters(state);
    this.advanceFSM(state, ticksCount, keypoints); // may emit error / success
    if (ticksCount === MAX_TICKS) {
      this.checkFinalSuccess();
    }
  }

  /* ---------------------- FSM / VALIDATION -------------------- */
  private advanceFSM(
    state: RotationState,
    tick: number,
    keypoints: KeyPoint[],
  ) {
    /* Unknown-frame & stall checks first */
    if (state === RotationState.Unknown) {
      if (++this.unknownTicks >= UNKNOWN_TICKS_LIMIT) {
        // use did-not-rotate-in-place instead because body not detected
        // could be confusing for users
        this.pushError("did-not-rotate-in-place", "Did not rotate in place");
      }
      return;
    } else {
      this.unknownTicks = 0;
    }

    /* FSM transitions */
    switch (this.phase) {
      case Phase.StartFront:
        if (state === RotationState.Front) return; // still aligning
        if (state === RotationState.Rotated) {
          this.phase = Phase.Side1;
          this.startTick = tick;
          return;
        }
        this.pushError("under-rotation", "Incomplete rotation");
        break;

      case Phase.Side1:
        if (state === RotationState.Rotated) return;
        if (state === RotationState.Back) {
          this.phase = Phase.Back;
          return;
        }
        this.checkRotateInPlace();
        break;

      case Phase.Back:
        if (state === RotationState.Back) {
          if (this.lastNorm > BACK_STATE_THRESHOLD) {
            const shouldersOutOfView = !keypoints.some(
              (p) =>
                (p.bodyPart === "left_shoulder" &&
                  p.coordinate.x !== 0 &&
                  p.coordinate.y !== 0) ||
                (p.bodyPart === "right_shoulder" &&
                  p.coordinate.x !== 0 &&
                  p.coordinate.y !== 0),
            );
            if (shouldersOutOfView) {
              this.pushError(
                "did-not-rotate-in-place",
                "Did not rotate in place",
              );
            }
          }
          return;
        }
        if (state === RotationState.Rotated) {
          this.phase = Phase.Side2;
          return;
        }
        this.pushError("over-rotation", "Over rotation");
        break;

      case Phase.Side2:
        if (state === RotationState.Rotated) return;
        if (state === RotationState.Back) return;
        if (state === RotationState.Front) {
          this.phase = Phase.EndFront;
          this.endTick = tick;
          return;
        }
        console.error("Invalid state: " + state);
        break;

      case Phase.EndFront:
        if (state !== RotationState.Front) {
          // forgive the first two wrong states
          if (this.wrongStates < MAX_WRONG_STATES) {
            this.wrongStates++;
            return;
          }
          this.pushError("over-rotation", "Over rotation");
          return;
        }
        if (this.endTick - this.startTick < MIN_ROTATION_FRAMES) {
          this.pushError("rotation-too-quick", "Rotated too quickly");
          return;
        }
        this.wrongStates = 0; // if front then reset wrong states
        break;
      case Phase.Done:
        /* ignore further frames after completion */
        break;
    }
  }

  private checkRotateInPlace() {
    // Check if user rotated in place, return false if not
    if (this.maxNorm > MAX_JOINT_DIST_THRESHOLD) {
      // user moved closer to camera while facing backwards
      this.pushError("did-not-rotate-in-place", "Did not rotate in place");
      return false;
    }

    if (this.minNorm < MIN_JOINT_DIST_THRESHOLD) {
      // user moved closer to camera while facing forwards
      this.pushError("did-not-rotate-in-place", "Did not rotate in place");
      return false;
    }

    if (
      // user moved further away from the camera while rotating
      this.lastNorm < -MID_ROTATION_THRESHOLD &&
      this.maxNorm > MID_ROTATION_THRESHOLD &&
      this.maxNorm < BACK_STATE_THRESHOLD
    ) {
      this.pushError("did-not-rotate-in-place", "Did not rotate in place");
      return false;
    }
    return true;
  }

  private checkFinalSuccess() {
    if (!this.checkRotateInPlace()) return;

    if (this.startTick === -1 || this.endTick === -1) {
      this.pushError("under-rotation", "Incomplete rotation");
      return;
    }
    if (this.endTick - this.startTick < MIN_ROTATION_FRAMES) {
      this.pushError("rotation-too-quick", "Rotated too quickly");
      return;
    }

    /* SUCCESS */
    logInfo("[RotationManager] checkFinalSuccess — rotation succeeded", {
      startTick: this.startTick,
      endTick: this.endTick,
      rotationFrames: this.endTick - this.startTick,
    });
    this.phase = Phase.Done;
    this.emit(RotationEvents.SUCCESS, {
      ...this.latestPoseEvt!,
      state: RotationState.Front,
      gravityString: this.gravityString,
      keypoints: this.toKeyPoints(this.latestPoseEvt!.poses[0]),
      lastNorm: this.lastNorm,
    });
    this.emit(RotationEvents.COMPLETED, undefined);
    this.reset();
  }

  /* ------------------ stall / counters helpers ---------------- */
  private updateStallCounters(state: RotationState) {
    this.sameStateTicks =
      state === this.lastState ? this.sameStateTicks + 1 : 1;
    this.lastState = state;
  }

  /* --------------------- ERROR EMITTER ------------------------ */
  private pushError(code: ScanRotationError["code"] | string, message: string) {
    logInfo("[RotationManager] pushError — emitting error and resetting", {
      code,
      message,
      phase: this.phase,
      lastState: this.lastState,
      startTick: this.startTick,
      endTick: this.endTick,
      unknownTicks: this.unknownTicks,
    });
    // Log detailed motion violation info when device-moved error occurs
    if (code === "device-moved") {
      const violation = getLastMotionViolation();
      if (violation) {
        logInfo(
          "[RotationManager] Device moved during scan - violation details",
          {
            detected: violation.detected,
            timestamp: new Date(violation.timestamp).toISOString(),
          },
        );

        // Log each violation detail separately
        violation.details.forEach((detail) => {
          logInfo(`[RotationManager] ${detail.type} exceeded threshold`, {
            value: detail.value,
            threshold: detail.threshold,
            unit: detail.unit,
          });
        });

        clearLastMotionViolation();
      }
    }

    this.emit(RotationEvents.ERROR, { code, message } as ScanRotationError);
    this.emit(RotationEvents.COMPLETED, undefined);
    this.internalError = { code, message } as ScanRotationError;
    // Before the reset: from here on, this spin's replies still in flight are
    // dropped by `processPose` rather than filed into the next spin's upload.
    this.poseDetectionMgr.closeRotationRun();
    this.reset();
  }

  /* ---------------------- CLASSIFICATION ---------------------- */
  private classifyState(pts: KeyPoint[]): RotationState {
    const ratios: number[] = [];
    const init = { ...this.jointsInit };
    for (let i = 0; i < LEFT_PARTS.length; i++) {
      const l = pts.find((p) => p.bodyPart === LEFT_PARTS[i]);
      const r = pts.find((p) => p.bodyPart === RIGHT_PARTS[i]);
      if (l && r && l.coordinate.x && r.coordinate.x) {
        const d = r.coordinate.x - l.coordinate.x;
        if (!init[LEFT_PARTS[i]]) init[LEFT_PARTS[i]] = Math.abs(d);
        ratios.push(d / init[LEFT_PARTS[i]]);
      }
    }
    this.jointsInit = init;
    if (!ratios.length) return RotationState.Unknown;

    let m = this.median(ratios);
    const aboveFlipCorrectionThreshold =
      Math.min(Math.abs(this.lastNorm), Math.abs(m)) >
      FLIP_CORRECTION_THRESHOLD;
    if (
      aboveFlipCorrectionThreshold &&
      Math.abs(m - this.lastNorm) > Math.abs(-m - this.lastNorm)
    ) {
      m *= -1; // Correct front-back flips by flipping the sign
    }
    this.lastNorm = m;
    this.maxNorm = Math.max(this.maxNorm, m);
    this.minNorm = Math.min(this.minNorm, m);

    if (m < FORWARD_STATE_THRESHOLD) return RotationState.Front;
    if (m > BACK_STATE_THRESHOLD) return RotationState.Back;
    return RotationState.Rotated;
  }

  /* ------------------------ UTILITIES ------------------------- */
  private median(a: number[]) {
    const s = [...a].sort((x, y) => x - y);
    const m = s.length >> 1;
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  }
  private toKeyPoints(p?: any): KeyPoint[] {
    if (!p || p.score < 0.35) return [];
    return p.keypoints
      .filter((k: any) => k.name)
      .map((k: any) => ({
        bodyPart: k.name,
        coordinate: { x: k.x, y: k.y },
      }));
  }

  /* ------------------------- RESET ---------------------------- */
  private reset() {
    logInfo("[RotationManager] reset() called", {
      hadError: !!this.internalError,
      errorCode: this.internalError?.code,
      errorMessage: this.internalError?.message,
      phase: this.phase,
      startTick: this.startTick,
      endTick: this.endTick,
    });
    this.phase = Phase.StartFront;
    this.sameStateTicks = this.unknownTicks = 0;
    this.lastState = RotationState.Unknown;
    this.startTick = this.endTick = -1;
    this.maxNorm = Number.NEGATIVE_INFINITY;
    this.minNorm = Number.POSITIVE_INFINITY;
    this.lastNorm = -1;
    this.jointsInit = {};
    this.latestPoseEvt = null;
    this.internalError = null;
    this.stationary = true;
    this.gravityString = "";
    clearLastMotionViolation(); // Clear stored violation data for next scan
    this.wrongStates = 0;
    this.emit(RotationEvents.RESET, undefined);
  }
}
